import { create } from 'zustand';
import { api } from '../services/apiService';
import type { Project, ProjectWithTasks } from '../types/projects';

interface ProjectFilters {
  status?: string;
  department?: string;
  lead_agent?: string;
  search?: string;
}

interface ProjectState {
  projects: Project[];
  selectedProject: ProjectWithTasks | null;
  loading: boolean;
  detailLoading: boolean;
  filters: ProjectFilters;
  fetchProjects: () => Promise<void>;
  fetchProject: (id: number) => Promise<void>;
  createProject: (data: Partial<Project>) => Promise<Project>;
  updateProject: (id: number, data: Partial<Project>) => Promise<Project>;
  deleteProject: (id: number) => Promise<void>;
  setFilters: (filters: Partial<ProjectFilters>) => void;
  clearSelectedProject: () => void;
}

function buildQueryString(filters: ProjectFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.department) params.set('department', filters.department);
  if (filters.lead_agent) params.set('lead_agent', filters.lead_agent);
  if (filters.search) params.set('search', filters.search);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  selectedProject: null,
  loading: false,
  detailLoading: false,
  filters: {},

  fetchProjects: async () => {
    try {
      set({ loading: true });
      const qs = buildQueryString(get().filters);
      const projects = await api.get<Project[]>(`/projects${qs}`);
      set({ projects, loading: false });
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      set({ loading: false });
    }
  },

  fetchProject: async (id: number) => {
    try {
      set({ detailLoading: true });
      const project = await api.get<ProjectWithTasks>(`/projects/${id}`);
      set({ selectedProject: project, detailLoading: false });
    } catch (err) {
      console.error('Failed to fetch project:', err);
      set({ detailLoading: false });
    }
  },

  createProject: async (data: Partial<Project>) => {
    const project = await api.post<Project>('/projects', data);
    await get().fetchProjects();
    return project;
  },

  updateProject: async (id: number, data: Partial<Project>) => {
    const project = await api.patch<Project>(`/projects/${id}`, data);
    await get().fetchProjects();
    return project;
  },

  deleteProject: async (id: number) => {
    await api.delete(`/projects/${id}`);
    set({ selectedProject: null });
    await get().fetchProjects();
  },

  setFilters: (newFilters: Partial<ProjectFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
    get().fetchProjects();
  },

  clearSelectedProject: () => {
    set({ selectedProject: null });
  },
}));
